"use client";

import { useEffect, useState } from "react";
import { Menu, LogOut, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-1">
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-0">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground relative px-4 py-2 text-sm font-default font-medium tracking-widest uppercase transition-colors duration-300 hover:text-primary group inline-flex items-center">
                {item.label}
                <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center rounded-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 pt-4">
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 pt-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="text-foreground text-base font-default font-medium px-3 py-3 rounded-md hover:bg-accent hover:text-primary transition-colors duration-300">
          {item.label}
        </Link>)}
    </nav>;
}
function AuthForm({
  mode,
  setMode
}: {
  mode: 'login' | 'signup';
  setMode: (m: 'login' | 'signup') => void;
}) {
  const {
    control,
    handleSubmit,
    isSubmitting
  } = useWvcForm();
  return <Form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^\S+@\S+$/i,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/70">Email Address</FormLabel>
            <FormControl>
              <Input placeholder="name@example.com" {...field} className="bg-background border-primary/20 focus:border-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      <FormField control={control} name="password" rules={{
      required: "Password is required",
      minLength: {
        value: 6,
        message: "Min 6 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/70">Password</FormLabel>
            <FormControl>
              <Input type="password" placeholder="••••••••" {...field} className="bg-background border-primary/20 focus:border-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      {mode === 'signup' && <FormField control={control} name="confirm_password" rules={{
      required: "Please confirm your password"
    }} render={({
      field
    }) => <FormItem>
              <FormLabel className="text-foreground/70">Confirm Password</FormLabel>
              <FormControl>
                <Input type="password" placeholder="••••••••" {...field} className="bg-background border-primary/20 focus:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />}
      <Button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-[#B89B5F] to-[#D4AF37] text-white font-bold py-6 rounded-lg shadow-lg hover:scale-[1.02] transition-transform">
        {mode === 'login' ? 'Sign In' : 'Create Account'}
      </Button>
      <div className="text-center mt-4">
        <Button type="button" variant="link" onClick={() => setMode(mode === 'login' ? 'signup' : 'login')} className="text-sm text-primary hover:underline font-medium p-0 h-auto">
          {mode === 'login' ? "Don't have an account? Sign Up" : "Already have an account? Login"}
        </Button>
      </div>
    </Form>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="230721" id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 ${scrolled ? "bg-background/80 backdrop-blur-lg border-b border-primary/20 shadow-lg" : "bg-[#0A0A0A] border-b border-primary/10"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/HomePage#" className="flex items-center shrink-0">
            <img src="https://shifahydrationlounge.com/wp-content/uploads/2026/05/cropped-cropped-cropped-IMG_8807.jpg" alt="Al Shifa Hydration Lounge" className="h-10 md:h-12" />
          </Link>

          <div className="hidden md:flex items-center flex-1 justify-center">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="hidden md:flex items-center gap-3 shrink-0">
            {isLoggedIn ? <Button variant="ghost" onClick={() => setIsLoggedIn(false)} className="text-foreground text-sm font-default font-semibold tracking-widest uppercase px-3 py-2 hover:text-primary transition-colors duration-300 flex items-center gap-2">
                <LogOut className="size-4" />
                Logout
              </Button> : <Sheet open={authOpen} onOpenChange={setAuthOpen}>
                <SheetTrigger asChild>
                  
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-md p-8 bg-background/95 backdrop-blur-xl border-l border-primary/20">
                  <SheetHeader className="mb-8">
                    <SheetTitle className="text-2xl font-serif text-primary">
                      {authMode === 'login' ? 'Welcome Back' : 'Join Al Shifa'}
                    </SheetTitle>
                  </SheetHeader>
                  <FormProvider formId="member-auth-form" sectionName="header-auth" defaultValues={{
                email: "",
                password: "",
                confirm_password: ""
              }} submitText={authMode === 'login' ? 'Login' : 'Sign Up'}>
                    <AuthForm mode={authMode} setMode={setAuthMode} />
                  </FormProvider>
                </SheetContent>
              </Sheet>}
            <Button asChild size="sm" className="bg-gradient-to-r from-[#B89B5F] to-[#D4AF37] text-white hover:opacity-90 font-default font-bold tracking-wider px-6 rounded-full shadow-md transition-all duration-300 hover:scale-105 animate-pulse-subtle">
              <Link to="#">Book Now</Link>
            </Button>
          </div>

          <div className="flex md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground">
                  <Menu className="size-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-6 bg-background/95 backdrop-blur-xl border-l border-primary/20">
                <div className="flex items-center justify-between mb-2">
                  <Link to="/" onClick={() => setMobileOpen(false)}>
                    <img src="/img/al_shifa_luxury_logo.png" alt="Al Shifa Hydration Lounge" className="h-8" />
                  </Link>
                </div>
                <MenuProvider menu_id="16">
                  <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                </MenuProvider>
                <div className="flex flex-col gap-3 mt-6 pt-6 border-t border-primary/10">
                  {isLoggedIn ? <Button variant="outline" onClick={() => {
                  setIsLoggedIn(false);
                  setMobileOpen(false);
                }} className="w-full border-primary/20 text-foreground font-semibold tracking-widest uppercase py-6 rounded-md flex items-center justify-center gap-2">
                      <LogOut className="size-4" />
                      Logout
                    </Button> : null}
                  <Button asChild size="default" className="bg-gradient-to-r from-[#B89B5F] to-[#D4AF37] text-white hover:opacity-90 font-default font-bold tracking-wider rounded-full w-full shadow-lg py-6" onClick={() => setMobileOpen(false)}>
                    <Link to="#">Book Now</Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}